import { motion } from "motion/react";
import { Mail, Phone, Building2, Factory, MessageSquare, ArrowRight } from "lucide-react";

const EMAIL = "cmd@orangecurrent.com";
const PHONE = "+91 8655636529";

export function ContactSection() {
  const targetAudience = [
    {
      icon: Building2,
      title: "Corporate Consumers",
      description: "ESG-focused enterprises seeking verified renewable energy solutions",
      gradient: "from-emerald-500 to-emerald-600"
    },
    {
      icon: Factory,
      title: "IPP Consumers",
      description: "Independent Power Producers requiring green energy certificates",
      gradient: "from-cyan-500 to-cyan-600"
    }
  ];

  const contactMethods = [
    {
      icon: Mail,
      label: "Email Us",
      value: EMAIL,
      link: `mailto:${EMAIL}`
    },
    {
      icon: Phone,
      label: "Call Us",
      value: PHONE,
      link: `tel:${PHONE}`
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
            Green Solutions for Your Business
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tailored renewable energy solutions for Corporate and IPP consumers
          </p>
        </motion.div>

        {/* Target Audience Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
          {targetAudience.map((audience, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 hover:border-emerald-300 transition-all hover:shadow-xl h-full">
                <div className={`w-14 h-14 bg-gradient-to-br ${audience.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <audience.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl mb-2 text-gray-900">
                  {audience.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {audience.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24"></div>

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <MessageSquare className="w-8 h-8" />
                <h3 className="text-2xl md:text-3xl">Get in Touch</h3>
              </div>

              <p className="text-lg opacity-90 mb-8 max-w-2xl">
                Our team of green energy experts is ready to help you transition to 100% renewable energy.
                Contact us today to discuss custom solutions for your organization.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {contactMethods.map((method, index) => (
                  <a
                    key={index}
                    href={method.link}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                        <method.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm opacity-90 mb-1">{method.label}</p>
                        <p className="text-lg">{method.value}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </a>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-8 pt-8 border-t border-white/20">
                <div className="grid md:grid-cols-3 gap-6 text-center md:text-left">
                  <div>
                    <p className="text-sm opacity-90 mb-1">Response Time</p>
                    <p className="text-lg">Within 24 hours</p>
                  </div>
                  <div>
                    <p className="text-sm opacity-90 mb-1">Working Hours</p>
                    <p className="text-lg">Mon - Sat, 9AM - 6PM</p>
                  </div>
                  <div>
                    <p className="text-sm opacity-90 mb-1">Support</p>
                    <p className="text-lg">Dedicated Account Manager</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-xl">✓</span>
            </div>
            <p className="text-sm text-gray-600">Custom pricing for bulk consumers</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-xl">✓</span>
            </div>
            <p className="text-sm text-gray-600">Flexible contract terms available</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-xl">✓</span>
            </div>
            <p className="text-sm text-gray-600">Complete compliance documentation</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
